<template>
  <AnalyticsCard layout="vertical" :title="action === SummaryAction.Reported ? $t('Dashboard.Charts.ClickRateByCampaignReported') : $t('Dashboard.Charts.ClickRateByCampaign')" :chart-height="chartHeight" :showLoader="isLoading">
    <template #chart>
      <BarChart
        v-if="!isLoading && clickRateByCampaign.datasets?.length && clickRateByCampaign.datasets[0].data.length"
        :datasets="clickRateByCampaign.datasets"
        :labels="clickRateByCampaign.labels"
        show-x-border
        show-y-labels
        show-x-labels
        show-x-intra-lines
        show-y-intra-lines
        :chart-options="options"
        :aspect-ratio="aspectRatio"
      />
      <div v-if="!isLoading && clickRateByCampaign.datasets?.length && !clickRateByCampaign.datasets[0].data.length"
        class="!min-h-[13rem] flex font-bold text-xl">
        <div class="m-auto"> <Txt bold>{{ $t('Dashboard.Charts.NoData') }}</Txt> </div>
      </div>
    </template>
    <template v-if="isAdmin && isClicked" #menu-items>
      <MenuItem @click="showAvgDiff('click-rate')">{{$t('Dashboard.Charts.ClickRateByCampaign')}}</MenuItem>
      <MenuItem @click="showAvgDiff('avg-rand')">{{$t('Dashboard.Charts.MlEfficiency')}}</MenuItem>
    </template>
  </AnalyticsCard>
</template> 

<script>
import phishingService from '@/services/phishing.service.js'
import colors from '@/utils/colors'
import { Roles, SummaryAction } from '@/common/constants'
import { defineComponent } from 'vue';

export default defineComponent({
  data() {
    return {
      isLoading: false,
      title: this.$t("Dashboard.Charts.ClickRateByCampaign"),
      options: {},
      averageDifficultyDataset: {},
      clickRateByCampaign : {},
      counter: 0,
      isReported: false,
      isClicked: false,
      SummaryAction
    }
  },

  computed: {
    isAdmin() {
      return this.$profile.hasRoles(Roles.Admin);
    }
  },

  props: {
    chartHeight : Number,
    aspectRatio: Number, 
    apiFilter: Object,
    companyData: Object,
    action: {
      type: String,
      default: SummaryAction.Clicked
    }
  },

  watch: {
    '$props.apiFilter': {
      async handler() {
        await this.transformData()
        this.setOptions();
      },
      deep: true
    }
  },

  async mounted() {
    this.isReported = this.action === SummaryAction.Reported;
    this.isClicked = this.action === SummaryAction.Clicked;
    await this.transformData();
    this.setOptions();
  },

  methods : {
    initProps() {
      const createDataset = (type, label, backgroundColor, borderColor, chartMode) => ({
        type,
        label,
        backgroundColor,
        borderColor,
        borderWidth: 2,
        fill: false,
        data: [],
        sent: [],
        clicked: [],
        avg_difficulty: [],
        efficiency: [],
        chartMode,
        options: {
          responsive: true,
          maintainAspectRatio: false
        },
        yAxisID: 'y1',
        spanGaps: true,
        hidden: false,
      });

      this.averageDifficultyDataset = createDataset('line', '', colors['green'][500], colors['green'][700], 'avg-diff');
      this.efficiencyDataset = createDataset('line', '', colors['accent'][500], colors['accent'][700], 'avg-rand');
      
      this.clickRateByCampaign = {
        datasets: [
          {
            label: '',
            chartMode: 'click-rate',
            backgroundColor: this.isReported ? colors['cyan'][600] : colors['soft-blue'][500],
            data: [],
            sent: [],
            clicked : [],
            avg_difficulty : [],
            period: [],
            reported: [],
            hidden: false
          }
        ],
        labels: [],
        yAxisID: 'y',
      };
    },
    showAvgDiff(showVal) {
      this.clickRateByCampaign.datasets.forEach((dataset) => {
        dataset.hidden = dataset.chartMode === showVal ? !dataset.hidden : dataset.hidden;
      });

      this.setOptions();
      this.$forceUpdate();
    },
    async transformData() {
      try {
        this.counter++;
        
        this.isLoading = true;
        const result = await phishingService.getCompanySummary(this.companyData.company_id, { code: 'campaign', ...this.apiFilter });
        const campaigns = [...result.data].slice().reverse();

        this.initProps();
    
        for(const c of campaigns) {
          this.clickRateByCampaign.labels.push(c.name)
          if(this.isReported){
            this.clickRateByCampaign.datasets[0].data.push(c.reported)
          } else if(this.isClicked){
            this.clickRateByCampaign.datasets[0].data.push(((c.clicked/c.sent)*100).toFixed(1))
          }
          this.clickRateByCampaign.datasets[0].sent.push(c.sent)
          this.clickRateByCampaign.datasets[0].clicked.push(c.clicked)
          this.clickRateByCampaign.datasets[0].avg_difficulty.push(c.avg_difficulty)
          let start = this.$dateTime.fromFormat(c.planned_start, 'yyyy-MM-dd HH:mm:ss').toFormat('dd.MM.yyyy');
          let end = this.$dateTime.fromFormat(c.planned_end, 'yyyy-MM-dd HH:mm:ss').toFormat('dd.MM.yyyy');
          this.clickRateByCampaign.datasets[0].period.push(start + " - " + end);
          this.clickRateByCampaign.datasets[0].reported.push(c.reported)

          this.averageDifficultyDataset.data.push(c.avg_difficulty)
          this.averageDifficultyDataset.sent.push(c.sent)
          this.averageDifficultyDataset.clicked.push(c.clicked)
          this.averageDifficultyDataset.avg_difficulty.push(c.avg_difficulty)

          // Efficiency Dataset
          let efficiency = 0;
          if(c.avg_probability_not_random === 0.1) { 
            efficiency = null;
          } else if (c.avg_probability_random && c.avg_probability_not_random) {
            efficiency = ((c.avg_probability_not_random / c.avg_probability_random) - 1) * 100;
            // Negative values for efficiency must be set to 0
            if(efficiency < 0) { 
              efficiency = 0; 
            }
          }

          this.efficiencyDataset.data.push(efficiency)
          this.efficiencyDataset.sent.push(c.sent)
          this.efficiencyDataset.clicked.push(c.clicked)
          this.efficiencyDataset.hidden = this.isAdmin && this.isClicked ? false : true
          this.efficiencyDataset.efficiency.push(efficiency)
        }

        this.clickRateByCampaign.datasets.unshift(this.efficiencyDataset)
      } catch (error) {
        this.$eventBus.$emit('show-alert', {title: this.$t('General.DataFetchError'), variant: 'danger'});
      } finally {
        if (this.counter > 0) this.counter--;
        
        if (!this.counter) this.isLoading = false;
      }
    },
    formatValue(value) {
      const numValue = Number(value.replace(/,/g, '.'));
      return !isNaN(numValue) ? numValue.toFixed(0) + '%' : '';
    },
    setOptions() {
      const yAxesVisibility = this.clickRateByCampaign.datasets.reduce((acc, dataset) => {
        acc[dataset.chartMode] = !dataset.hidden;
        return acc;
      }, {});

      // Hide ML efficiency scale entirely for reported-by-campaign mode
      if (this.isReported) {
        yAxesVisibility['avg-rand'] = false;
      }

      this.options = {
        scales: {
          y: {
            min: 0,
            display: yAxesVisibility['click-rate'],
            ticks: {
              callback: value => this.isClicked ? `${value}%` : `${value}`
            },
          },
          y1: {
            min: 0,
            display: yAxesVisibility['avg-rand'],
            position: 'right',
            ticks: {
              callback: value => `${value}%`,
              color: "black"
            },
            title: {
              color: "black"
            }
          }
        },
        plugins: {
          tooltip: {
            callbacks : {
              label: (tooltipItem) => {
                if(tooltipItem.dataset.chartMode && tooltipItem.dataset.chartMode=='avg-diff') {
                  return this.$t('Dashboard.Charts.AverageDifficulty')+ ": "+ tooltipItem.formattedValue
                } else if(tooltipItem.dataset.chartMode && tooltipItem.dataset.chartMode=='avg-rand') {
                  return this.$t('Dashboard.Charts.AverageProbabilityNotRandom')+ ": "+ this.formatValue(tooltipItem.formattedValue)
                } else {
                  return this.$t('Dashboard.Charts.' + (this.isReported ? 'ReportedRate' : 'SuccessfulAttacks')) + ": " + tooltipItem.formattedValue + (this.isReported ? "" : "%");
                }
              },
              afterBody: (tooltipItem) => {
                if(tooltipItem[0].dataset.chartMode == 'click-rate') {
                  let body = []
                  // let datasetIndex = tooltipItem[0].datasetIndex;
                  let index = tooltipItem[0].dataIndex;
                  let sent = tooltipItem[0].dataset.sent[index];
                  let clicked = tooltipItem[0].dataset.clicked[index];
                  let avg_difficulty = tooltipItem[0].dataset.avg_difficulty[index];
                  let period = tooltipItem[0].dataset.period[index];
                  let reported = tooltipItem[0].dataset.reported[index];
                  
                  body.push("");
                  body.push(this.$t('Dashboard.Charts.AttacksSent') + ": " + sent);
                  this.isReported ? body.push(this.$t('Dashboard.Charts.Tooltip.Reported') + ": " + reported) : body.push(this.$t('Dashboard.Charts.Clicked') + ": " + clicked);
                  if (this.isClicked) body.push(this.$t('Dashboard.Charts.AverageDifficulty') + ": " + Number(avg_difficulty));
                  body.push(this.$t('Dashboard.Charts.CampaignPeriod') + ": " + period);

                  return body;
                }
              },
            },
          },
        },
      };
    },
  },
});
</script>